import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Privacy Policy. Plain-English copy describing what FlareCode collects,
 * how it's used, and what rights users have. Aimed at a hobby-project
 * audience but thorough enough that sharing the URL with strangers isn't
 * embarrassing.
 *
 * NOT LEGAL ADVICE. If FlareCode adds advertising, third-party analytics,
 * or commercial features, this policy needs a rewrite.
 */

const LAST_UPDATED = '2026-04-19';

const PRIVACY_MD = `
# Privacy Policy

**Last updated:** ${LAST_UPDATED}

This policy describes what data FlareCode collects, how we use it, and what
you can do about it. Short version: we collect what's needed to run the
service, nothing for advertising, and we don't sell anything.

## 1. What we collect

When you create an account:

- **Email** — for login and, if we ever add password reset, recovery. We
  never display your email publicly.
- **Username** — stored lowercase, shown publicly on your submissions and
  profile.
- **Password hash** — a bcrypt hash; we never store your actual password.

When you use the service:

- **Submissions**: the problem you solved, the language, the source code,
  the verdict, and per-case runtime/memory.
- **Drafts**: code you type but haven't submitted yet, per problem + language.
- **Session cookie**: a single \`flarecode_session\` cookie holding an
  opaque random token. HttpOnly, Secure, SameSite=Lax.

## 2. What we don't collect

- No third-party analytics (no Google Analytics, Plausible, etc.)
- No tracking pixels or advertising IDs
- No access to your location, camera, microphone, or other sensors
- No browser fingerprinting beyond standard HTTP access logs

## 3. How we use your data

- To run the service: show you problems, judge submissions, display your
  profile
- To prevent abuse: rate-limit registrations, logins, and submissions using
  your IP address and/or user ID
- To debug problems: Cloudflare Workers observability captures log lines
  and traces for requests, which may incidentally include your IDs

We do not sell data, share it with advertisers, or give it to anyone outside
the service's infrastructure providers.

## 4. Cookies

FlareCode uses exactly one cookie: \`flarecode_session\`. It's set when you
log in and contains a random session identifier. It's HttpOnly (not visible
to JavaScript), Secure (sent over HTTPS only), SameSite=Lax (mitigates CSRF),
and expires 30 days after last use.

We don't use cookies for tracking or analytics.

## 5. Infrastructure

FlareCode runs entirely on Cloudflare. Your data is stored in:

- **Cloudflare D1** — user accounts, submissions metadata, drafts
- **Cloudflare R2** — submission source code, problem content
- **Cloudflare Durable Objects** — session coordination and live streaming

Cloudflare has its own privacy practices that govern the underlying storage.
See Cloudflare's Privacy Policy at <https://www.cloudflare.com/privacypolicy/>.

## 6. Your rights

You can:

- **See your data** by browsing your profile and submissions
- **Request deletion** of your account by emailing us. Account self-deletion
  isn't a built-in feature yet, so this happens manually. We'll confirm and
  remove your records within a reasonable timeframe
- **Export your submissions** on request

## 7. Children

FlareCode is intended for users 13 and older. We don't knowingly collect
data from children under 13. If you believe a child under 13 has created an
account, contact us and we'll remove it.

## 8. Changes

If we change this policy in a material way, the \`Last updated\` date above
will change. Continued use after changes means you accept the update.

## 9. Contact

Email the address on our GitHub profile or open an issue on the repository.
`;

export function PrivacyPage() {
  return (
    <section className="legal-page">
      <div className="markdown-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{PRIVACY_MD}</ReactMarkdown>
      </div>
    </section>
  );
}
